import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useUsersList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refUsersTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'name',
      label: 'user name',
      sortable: true,
    },
    {
      key: 'email',
      label: 'email',
      sortable: true,
    },
    {
      key: 'position',
      label: 'position',
      sortable: true,
    },
    {
      key: 'department',
      label: 'department',
      sortable: false,
    },
    {
      key: 'roles',
      label: 'roles',
      sortable: false,
    },
    {
      key: 'twoFA',
      label: '2FA Setup',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'created on',
      sortable: true,
    },
    {
      key: 'createdBy.name',
      label: 'created by',
      sortable: true,
    },
    {
      key: 'updatedAt',
      label: 'last updated',
      sortable: true,
    },
    // {
    //   key: 'updatedBy.name',
    //   label: 'last updated by',
    //   sortable: true,
    // },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalUsers = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'name')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref(route.query.status ?? null)
  const departmentFilter = ref([])
  if (Array.isArray(route.query.department)) {
    departmentFilter.value = route.query.department
  } else if (route.query.department) {
    departmentFilter.value = [route.query.department]
  }
  const teamFilter = ref(route.query.team ?? '')
  if (route.name === 'directory-teams-show') {
    teamFilter.value = route.params.id
  }
  const roleFilter = ref(route.query.role ?? '')
  if (route.name === 'directory-roles-show') {
    roleFilter.value = route.params.id
  }
  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refUsersTable.value ? refUsersTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUsersTable.value.refresh()
  }

  watch([searchQuery, statusFilter, roleFilter, departmentFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchUsers = (ctx, callback) => {
    store
      .dispatch('app-users/fetchUsers', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        role: roleFilter.value,
        team: teamFilter.value,
        department: departmentFilter.value,
      },
      { root: true })
      .then(response => {
        const { users, total } = response.data
        currentDataLength.value = users.length
        callback(users)
        totalUsers.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              role: roleFilter.value,
              team: teamFilter.value,
              department: departmentFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteUser = () => {
    store
      .dispatch('app-users/deleteUser', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'danger'
    if (status === 'pending approval') return 'warning'
    return 'primary'
  }

  return {
    fetchUsers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUsersTable,

    resolveUserStatusVariant,
    refetchData,

    toDeletedID,
    deleteUser,
    currentDataLength,

    // Extra Filters
    statusFilter,
    roleFilter,
    teamFilter,
    departmentFilter,
  }
}
